import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, MapPin, ArrowRight, Sprout, Building2, Scissors, Trees, Users, User,
  Wheat, Bird, Gift, Heart, HeartHandshake, Handshake,
} from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/auth/AuthContext";
import { homeFaq } from "@/lib/homeFaq";
import HomeAtlasPreview from "@/components/HomeAtlasPreview";
import LandscapeSlideshow from "@/components/LandscapeSlideshow";
import BrandSymbol from "@/components/BrandSymbol";
import FaoCategoryCards from "@/components/FaoCategoryCards";
import { DataMessage } from "@/components/Editorial";
import { Button } from "@/components/ui/button";

const HERO = ["/landscapes/sicily-hero-v3.webp", "/landscapes/mediterranean-hero-v2.webp", "/landscapes/sicily-hero-v4.webp"];
const SUPPORT = ["/landscapes/sicily-olive-footer-v3.webp", "/landscapes/support-sapling-v2.webp", "/landscapes/sicily-footer-v4.webp"];

export default function Home() {
  const { lang, t } = useLang();
  const it = lang === "it";
  const navigate = useNavigate();
  const { user } = useAuth();
  const presentTo = user ? "/list-your-farm" : "/register";
  const [query, setQuery] = useState("");
  const [crops, setCrops] = useState(null);
  const [cropError, setCropError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setCropError(false); setCrops(null);
    api.get("/references/crop-categories", { signal: controller.signal })
      .then((r) => setCrops(r.data))
      .catch(() => { if (!controller.signal.aborted) setCropError(true); });
    return () => controller.abort();
  }, [attempt]);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(`/explore${query ? `?search=${encodeURIComponent(query)}` : ""}`);
  };

  const participants = [
    { icon: Sprout, t: it ? "Aziende agricole e zootecniche" : "Farms and livestock holdings", d: it ? "Coltivano, allevano, innovano" : "Cultivate, raise, innovate" },
    { icon: Building2, t: it ? "Comuni ed enti" : "Municipalities and public bodies", d: it ? "Tutela del territorio e beni comuni" : "Stewardship of land and common goods" },
    { icon: Scissors, t: it ? "Professionisti del verde" : "Green professionals", d: it ? "Progettano, curano, consigliano" : "Design, care, advise" },
    { icon: Trees, t: it ? "Parchi, giardini e centri educativi" : "Parks, gardens and educational centers", d: it ? "Conservazione, cura ed educazione" : "Conservation, care and education" },
    { icon: Users, t: it ? "Associazioni e fondazioni" : "Associations and foundations", d: it ? "Promuovono progetti e cultura" : "Promote projects and culture" },
    { icon: User, t: it ? "Cittadini e adottanti" : "Citizens and adopters", d: it ? "Sostengono, imparano, fanno la differenza" : "Support, learn, make a difference" },
  ];

  const heritage = [
    { icon: Wheat, t: it ? "Colture" : "Crops", d: it ? "Dai cereali agli ortaggi, dalla frutta alle piante industriali." : "From cereals to vegetables, fruit and industrial plants.", to: "/crops", images: ["/landscapes/sicily-vineyard-v3.webp", "/landscapes/crops-overview-v2.webp", "/landscapes/sicily-crops-v4.webp"] },
    { icon: Trees, t: it ? "Piante e alberature" : "Plants and trees", d: it ? "Specie spontanee e coltivate, alberi monumentali, giardini." : "Wild and cultivated species, monumental trees, gardens.", to: "/explore?activity=environment", images: ["/landscapes/sicily-garden-v3.webp", "/landscapes/plants-garden-v2.webp", "/landscapes/sicily-trees-v4.webp"] },
    { icon: Bird, t: it ? "Animali e razze" : "Animals and breeds", d: it ? "Biodiversità zootecnica e razze autoctone." : "Livestock biodiversity and native breeds.", to: "/explore?activity=livestock", images: ["/landscapes/sicily-pasture-v3.webp", "/landscapes/sicily-hens-v4.webp", "/landscapes/sicily-animals-v4.webp"] },
  ];

  const formulas = [
    { icon: Gift, t: it ? "Adozione con benefici" : "Adoption with benefits", d: t("homePolish.benefitAdoption") },
    { icon: Heart, t: it ? "Sostegno simbolico" : "Symbolic support", d: it ? "Sostieni un soggetto o progetto che ti sta a cuore, senza benefici commerciali concordati." : "Support a subject or project you care about, without agreed commercial benefits." },
    { icon: HeartHandshake, t: it ? "Contributo a progetto" : "Project contribution", d: it ? "Contribuisci alla realizzazione di progetti concreti sul territorio." : "Contribute to concrete local projects." },
    { icon: Handshake, t: it ? "Sponsorizzazione" : "Sponsorship", d: it ? "Sostieni un'iniziativa con visibilità e condizioni concordate con il promotore." : "Back an initiative with visibility and terms agreed with the promoter." },
  ];

  return (
    <div className="pa-home" data-testid="home-page">
      {/* HERO */}
      <section className="pa-hero relative flex items-center">
        <LandscapeSlideshow slides={HERO} label={it ? "Paesaggi del patrimonio vivente" : "Living heritage landscapes"} lang={lang} className="absolute inset-0" eager />
        <div className="absolute inset-0 pa-hero-wash" />
        <div className="pa-banner-content relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-14">
          <div className="max-w-2xl fade-up">
            <p className="pa-home-description text-sm text-[#557448] mb-4">{it ? "Crea connessioni. Proteggi il patrimonio vivente" : "Build connections. Protect living heritage"}</p>
            <h1 className="pa-hero-title font-serif font-semibold text-[#293D2C] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {it ? "Adotta la natura." : "Adopt nature."}<br />
              <span className="text-[#C46A3A]">{it ? "Sostieni il futuro." : "Support the future."}</span>
            </h1>
            <p className="pa-home-description text-[#3F493B] font-sans text-base sm:text-lg mt-6 max-w-xl leading-relaxed">
              {t("homePolish.heroDescription")}
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-6">
              <Link to={presentTo} data-testid="hero-list-farm-link" className="inline-flex items-center gap-2 rounded-md bg-[#557448] text-white font-medium px-5 py-3 hover:bg-[#46633b]">
                {it ? "Presenta la tua realtà" : "Present your work"} <ArrowRight size={16} />
              </Link>
              <Link to="/atlas" data-testid="hero-explore-link" className="inline-flex items-center gap-2 rounded-md border border-[#557448] bg-[#FFFDF8] text-[#557448] px-5 py-3 hover:bg-[#EAF0E6]">
                <MapPin size={16} /> {it ? "Esplora l’Atlante" : "Explore the Atlas"}
              </Link>
            </div>
            <p className="text-sm text-[#3F493B] mt-3">{t("homePolish.heroPlanNote")}</p>
            <details className="mt-5 max-w-xl">
              <summary className="cursor-pointer text-sm text-[#3F493B] underline">{it ? "Cerca nell’Atlante" : "Search the Atlas"}</summary>
            <form onSubmit={submitSearch} className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6E6258]" />
                <input
                  data-testid="hero-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  aria-label={it ? "Cerca soggetti, specie o territori..." : "Search subjects, species or places..."}
                  placeholder={it ? "Cerca soggetti, specie o territori..." : "Search subjects, species or places..."}
                  className="w-full h-10 pl-11 pr-4 rounded-md bg-[#FFFDF8]/95 backdrop-blur text-[#2A2521] placeholder:text-[#9A8F80] font-sans text-sm focus:outline-none focus:ring-2 focus:ring-[#E3B24C] shadow-sm"
                />
              </div>
              <Button type="submit" data-testid="hero-search-btn" className="h-10 rounded-md bg-[#557448] hover:bg-[#46633b] text-[#F8F5EA] px-7 font-sans font-medium">
                {it ? "Cerca" : "Search"}
              </Button>
            </form>
            </details>
          </div>
        </div>
      </section>

      {/* CHI PARTECIPA */}
      <section className="grain-bg py-10 sm:py-12" data-testid="home-participants">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2521]">{it ? "Chi partecipa" : "Who takes part"}</h2>
          <p className="pa-home-description text-[#5C4636]/80 mt-2">{it ? "Unisciti a una comunità che valorizza e protegge il patrimonio vivente." : "Join a community that values and protects living heritage."}</p>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4 mt-8">
            {participants.map((p, i) => (
              <div key={i} className="pa-participant bg-[#FFFDF8] border border-[#E2DACB] rounded-lg p-5 text-center flex flex-col items-center">
                <BrandSymbol kind={["farm", "public", "care", "parks", "community", "citizen"][i]} size={64} circle className="mb-3" />
                <h3 className="font-serif font-semibold text-xl text-[#2A2521] leading-snug">{p.t}</h3>
                <p className="pa-home-description text-xs text-[#5C4636]/70 mt-1.5">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FFFDF8] py-10 sm:py-12" data-testid="home-get-started">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl">{it ? "Come iniziare" : "How to get started"}</h2>
          <ol className="grid md:grid-cols-3 gap-6 mt-6">
            {(it ? [
              ["Crea il tuo account gratuito", "Registrati come persona: lo stesso account ti permette di partecipare e gestire le tue schede."],
              ["Aggiungi la realtà che rappresenti", "Presenta la tua azienda, attività professionale, organizzazione o luogo che gestisci."],
              ["Completa e salva la scheda", "Sarà consultabile nell’Atlante. Puoi iniziare con Free e scegliere Pro quando ti serve."],
            ] : [
              ["Create your free account", "Register as a person: one account lets you participate and manage your profiles."],
              ["Add the organization or place you represent", "Present your farm, professional activity, organization or place you manage."],
              ["Complete and save the profile", "It will be available in the Atlas. Start with Free and choose Pro when you need it."],
            ]).map(([title, description], i) => <li key={title} className="rounded-lg border border-[#E2DACB] p-5"><div className="flex items-center gap-3"><BrandSymbol kind={["citizen", "institution", "document"][i]} size={48} circle /><span className="text-[#557448] font-semibold">{i + 1}</span></div><h3 className="font-serif text-2xl mt-2">{title}</h3><p className="pa-home-description mt-2 text-sm">{description}</p></li>)}
          </ol>
          <p className="pa-home-description text-sm mt-4">{t("homePolish.exploreWithoutAccount")}</p>
        </div>
      </section>
      <HomeAtlasPreview />

      {/* ESPLORA IL PATRIMONIO VIVENTE */}
      <section className="bg-[#FFFDF8] py-10 sm:py-12 border-y border-[#E2DACB]" data-testid="home-heritage">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2521]">{it ? "Esplora il patrimonio vivente" : "Explore living heritage"}</h2>
          <p className="pa-home-description text-[#5C4636]/80 mt-2">{it ? "Scopri la ricchezza di colture, piante, animali e territori in Italia e nel mondo." : "Discover the richness of crops, plants, animals and territories in Italy and worldwide."}</p>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            {heritage.map((h, i) => (
              <article key={i} className="pa-heritage-card group bg-[#FFFDF8] border border-[#E2DACB] rounded-lg overflow-hidden hover:border-[#8FA878] transition-colors">
                <LandscapeSlideshow slides={h.images} label={h.t} lang={lang} className="relative h-48" interval={9000 + i * 1000} />
                <Link to={h.to} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#557448]">
                <div className="p-5">
                  <h3 className="font-serif text-xl text-[#2A2521]">{h.t}</h3>
                  <p className="pa-home-description text-sm text-[#5C4636]/80 mt-1.5 leading-relaxed">{h.d}</p>
                  <span className="inline-flex items-center gap-1 text-[#557448] text-sm font-medium mt-3 group-hover:gap-2 transition-all">{it ? "Esplora" : "Explore"} <ArrowRight size={15} /></span>
                </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ESPLORA LE COLTURE (9 FAO) */}
      <section className="grain-bg py-10 sm:py-12" data-testid="explore-by-crop">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2521]">{it ? "Esplora le colture" : "Explore crops"}</h2>
              <p className="pa-home-description text-[#5C4636]/80 mt-2">{it ? "Naviga tra le principali categorie di colture secondo la classificazione FAO." : "Browse the main crop categories by the FAO classification."}</p>
            </div>
            <Link to="/crops" data-testid="view-all-crops" className="inline-flex items-center gap-1.5 text-[#557448] font-sans text-sm font-medium hover:gap-2.5 transition-all">
              {it ? "Vedi tutte le colture" : "See all crops"} <ArrowRight size={16} />
            </Link>
          </div>
          {(cropError || crops === null || crops.length === 0) && (
            <DataMessage error={cropError} onRetry={cropError ? () => setAttempt((a) => a + 1) : undefined} retryLabel={it ? "Riprova" : "Retry"}>
              {cropError ? (it ? "Catalogo non disponibile al momento." : "Catalog temporarily unavailable.") : (it ? "Caricamento…" : "Loading…")}
            </DataMessage>
          )}
          <FaoCategoryCards categories={crops || []} />
        </div>
      </section>


      {/* QUATTRO MODI PER PARTECIPARE */}
      <section className="bg-[#FFFDF8] py-10 sm:py-12 border-y border-[#E2DACB]" data-testid="home-formulas">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2521]">{it ? "Quattro formule di sostegno" : "Four ways to support"}</h2>
          <p className="pa-home-description text-[#5C4636]/80 mt-2">{it ? "Scegli come sostenere una realtà o un progetto. Visite, formazione e volontariato sono ulteriori opportunità di partecipazione." : "Choose how to support an organization, place or project. Visits, learning and volunteering are additional ways to participate."}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {formulas.map((f, i) => (
              <div key={i} className="bg-[#F8F5EA] border border-[#E2DACB] rounded-xl p-6">
                <BrandSymbol kind={["benefits", "heart", "contribution", "handshake"][i]} size={48} className="mb-3" />
                <h3 className="font-serif font-semibold text-2xl text-[#2A2521]">{f.t}</h3>
                <p className="pa-home-description text-sm text-[#5C4636]/80 mt-1.5 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FREE E PRO */}
      <section className="grain-bg py-10 sm:py-12" data-testid="home-plans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl text-[#2A2521]">{t("homePlansSummary.title")}</h2>
          <p className="pa-home-description text-[#5C4636] mt-2 max-w-3xl">{t("homePlansSummary.intro")}</p>
          <div className="grid sm:grid-cols-2 gap-5 mt-6 max-w-4xl">
            <article className="min-w-0 rounded-md border border-[#E2DACB] bg-[#FFFDF8] p-5">
              <h3 className="flex items-center gap-3 font-serif text-2xl text-[#2A2521]"><BrandSymbol kind="leaf" size={40} />Free</h3>
              <p className="pa-home-description text-[#5C4636] mt-3">{t("homePlansSummary.freeTagline")}</p>
              <p className="pa-home-description text-[#5C4636] mt-2">{t("homePlansSummary.freeDescription")}</p>
            </article>
            <article className="min-w-0 rounded-md border border-[#E2DACB] bg-[#FFFDF8] p-5">
              <h3 className="flex items-center gap-3 font-serif text-2xl text-[#2A2521]"><BrandSymbol kind="crown" size={40} />Pro</h3>
              <p className="text-lg font-semibold text-[#557448] mt-3" data-testid="home-pro-price">{t("planComparison.proAnnualPrice")}</p>
              <p className="pa-home-description text-[#5C4636] mt-2">{t("homePlansSummary.proDescription")}</p>
              <p className="text-sm leading-relaxed text-[#5C4636] mt-3" data-testid="home-pro-renewal">{t("homePlansSummary.renewal")}</p>
            </article>
          </div>
          <Link to="/plans" className="inline-flex items-center gap-2 mt-5 text-[#557448] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">
            {t("homePlansSummary.compare")} <ArrowRight size={16} aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-[#FFFDF8] py-10 sm:py-12" data-testid="home-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="font-serif text-3xl sm:text-4xl">{it ? "Prima di iniziare" : "Before you start"}</h2>
          {homeFaq[it ? "it" : "en"].map(([question, answer], i) => <details key={question} className="border-b border-[#E2DACB] py-4"><summary className="cursor-pointer font-serif text-xl"><span className="inline-flex items-center gap-3 align-middle max-w-[calc(100%-1.5rem)]"><BrandSymbol kind={["platform", "leaf", "path"][i]} size={40} className="shrink-0" /><span>{question}</span></span></summary><p className="pa-home-description mt-3 leading-relaxed">{i === 1 ? <>{t("homePlansSummary.freeFaq")} <Link to="/plans" className="text-[#557448] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{t("homePlansSummary.faqPlans")}</Link></> : answer}</p></details>)}
          <Link to="/faq" className="inline-flex mt-5 text-[#557448] underline">{it ? "Leggi tutte le FAQ" : "Read all FAQs"}</Link>
          <div className="mt-8"><h3 className="font-serif text-2xl">{it ? "Fai conoscere ciò di cui ti prendi cura" : "Share what you care for"}</h3><Link to={presentTo} className="inline-flex mt-4 rounded-md bg-[#557448] text-white px-5 py-3 hover:bg-[#46633b]">{it ? "Presenta la tua realtà — inizia gratis" : "Present your work — start for free"}</Link></div>
        </div>
      </section>

      {/* SOSTIENI PLANTADOPTING */}
      <section className="pa-support relative py-12 sm:py-14 overflow-hidden" data-testid="home-support">
        <LandscapeSlideshow slides={SUPPORT} label={it ? "Paesaggi per sostenere PlantAdopting" : "Landscapes supporting PlantAdopting"} lang={lang} className="absolute inset-0" interval={11000} />
        <div className="absolute inset-0 pa-support-wash" />
        <div className="pa-banner-content relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-xl">
            <h2 className="font-serif text-4xl sm:text-5xl font-semibold text-[#293D2C] leading-tight">{it ? "Sostieni PlantAdopting" : "Support PlantAdopting"}</h2>
            <p className="pa-home-description font-sans text-[#3F493B] mt-5 text-base leading-relaxed">
              {it ? "Contribuisci allo sviluppo della piattaforma e aiuta a crescere una rete sempre più ampia di persone, progetti e territori per un futuro più sostenibile." : "Contribute to the platform's development and help grow an ever-wider network of people, projects and territories for a more sustainable future."}
            </p>
            <Button asChild data-testid="support-cta" className="mt-8 h-12 rounded-full bg-[#E3B24C] hover:bg-[#d4a23e] text-[#2A2521] font-sans font-semibold px-7">
              <Link to="/support">{it ? "Scopri il progetto" : "Discover the project"}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}


